import React, { useCallback, useEffect } from 'react';

import { Controller, useForm } from 'react-hook-form';

import { Box, Stack, TextField, Typography } from '@mui/material';

import { UUID_REGEX } from '~/utils/isUUID';
import { toTitleCase } from '~/utils/toTitleCase';

import AudioPicker from './AudioPicker';

interface TemplateSchema {
  properties: Record<
    string,
    {
      type: string;
      format?: string;
      minLength?: number;
      maxLength?: number;
      minimum?: number;
      maximum?: number;
      [key: string]: unknown;
    }
  >;
  required: Array<string>;
  sourceVideoDuration?: number;
}

interface TemplateDataFormProps {
  schema: TemplateSchema;
  onDataChange: (data: Record<string, unknown>) => void;
  onValidityChange?: (isValid: boolean) => void;
  onAudioUrlChange?: (audioUrl: string | null) => void;
  initialData?: Record<string, unknown>;
  excludeFields?: string[];
}

function isAudioIdField(fieldName: string): boolean {
  return fieldName === 'audioId' || fieldName.endsWith('AudioId');
}

export default function TemplateDataForm({
  schema,
  onDataChange,
  onValidityChange,
  onAudioUrlChange,
  initialData = {},
  excludeFields = [],
}: TemplateDataFormProps) {
  const {
    control,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: initialData,
    mode: 'onChange',
  });

  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  useEffect(() => {
    const subscription = watch((value) => {
      const processedData = { ...value };

      Object.entries(schema.properties).forEach(([fieldName, fieldSchema]) => {
        if (
          fieldSchema.type === 'number' &&
          processedData[fieldName] !== undefined &&
          processedData[fieldName] !== ''
        ) {
          const numValue = Number(processedData[fieldName]);
          if (!isNaN(numValue)) {
            processedData[fieldName] = numValue;
          }
        }
      });

      onDataChange(processedData);
    });
    return () => subscription.unsubscribe();
  }, [watch, onDataChange, schema.properties]);

  const handleAudioSelect = useCallback(
    (fieldName: string, assetId: string, url: string | null) => {
      setValue(fieldName, assetId, { shouldValidate: true });
      onAudioUrlChange?.(url);
    },
    [setValue, onAudioUrlChange],
  );

  const generateValidationRules = (fieldName: string, fieldSchema: TemplateSchema['properties'][string]) => {
    const rules: Record<string, unknown> = {};

    if (schema.required.includes(fieldName)) {
      rules.required = `${fieldName} is required`;
    }

    if (isAudioIdField(fieldName)) {
      if (schema.required.includes(fieldName)) {
        rules.validate = {
          notEmpty: (value: string) => (value && value.trim().length > 0) || `${fieldName} is required`,
        };
      }
      return rules;
    }

    if (fieldSchema.type === 'string') {
      if (fieldSchema.format === 'uuid') {
        rules.pattern = {
          value: UUID_REGEX,
          message: `${fieldName} must be a valid UUID format`,
        };
      }

      if (fieldSchema.minLength) {
        rules.minLength = {
          value: fieldSchema.minLength,
          message: `${fieldName} must be at least ${fieldSchema.minLength} characters`,
        };
      }

      if (fieldSchema.maxLength) {
        rules.maxLength = {
          value: fieldSchema.maxLength,
          message: `${fieldName} must be no more than ${fieldSchema.maxLength} characters`,
        };
      }
    }

    if (fieldSchema.type === 'number') {
      rules.validate = {
        isNumber: (value: string) => {
          if (!value) return true;
          const num = Number(value);
          if (isNaN(num)) return `${fieldName} must be a valid number`;
          return true;
        },
        minimum: (value: string) => {
          if (!value || fieldSchema.minimum === undefined) return true;
          const num = Number(value);
          if (isNaN(num)) return true;
          if (num < fieldSchema.minimum) return `${fieldName} must be at least ${fieldSchema.minimum}`;
          return true;
        },
        maximum: (value: string) => {
          if (!value || fieldSchema.maximum === undefined) return true;
          const num = Number(value);
          if (isNaN(num)) return true;
          if (num > fieldSchema.maximum) return `${fieldName} must be no more than ${fieldSchema.maximum}`;
          return true;
        },
      };
    }

    return rules;
  };

  const generateFieldLabel = (fieldName: string, fieldSchema: TemplateSchema['properties'][string]) => {
    const isRequired = schema.required.includes(fieldName);
    let label = toTitleCase(fieldName);

    if (fieldSchema.format) {
      label += ` (${fieldSchema.format})`;
    } else if (fieldSchema.type === 'number') {
      label += ` (number)`;
    }

    return isRequired ? `${label} *` : label;
  };

  const generateHelperText = (fieldName: string, fieldSchema: TemplateSchema['properties'][string]) => {
    const parts = [];

    if (fieldSchema.type === 'string') {
      if (fieldSchema.minLength && fieldSchema.maxLength) {
        parts.push(`Length: ${fieldSchema.minLength}-${fieldSchema.maxLength} characters`);
      } else if (fieldSchema.minLength) {
        parts.push(`Min length: ${fieldSchema.minLength} characters`);
      } else if (fieldSchema.maxLength) {
        parts.push(`Max length: ${fieldSchema.maxLength} characters`);
      }

      if (fieldSchema.format === 'uuid') {
        parts.push('Must be a valid UUID');
      }
    }

    if (fieldSchema.type === 'number') {
      if (fieldSchema.minimum !== undefined && fieldSchema.maximum !== undefined) {
        parts.push(`Range: ${fieldSchema.minimum}-${fieldSchema.maximum}`);
      } else if (fieldSchema.minimum !== undefined) {
        parts.push(`Minimum: ${fieldSchema.minimum}`);
      } else if (fieldSchema.maximum !== undefined) {
        parts.push(`Maximum: ${fieldSchema.maximum}`);
      }
    }

    return parts.join(', ');
  };

  const generatePlaceholder = (fieldSchema: TemplateSchema['properties'][string]) => {
    if (fieldSchema.format === 'uuid') {
      return 'UUID format';
    }
    if (fieldSchema.type === 'number') {
      return 'Enter a number';
    }
    return undefined;
  };

  if (!schema?.properties) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          No schema properties found
        </Typography>
      </Box>
    );
  }

  const visibleFields = Object.entries(schema.properties).filter(([fieldName]) => !excludeFields.includes(fieldName));

  return (
    <Stack spacing={2}>
      {visibleFields.map(([fieldName, fieldSchema]) => {
        if (isAudioIdField(fieldName)) {
          return (
            <Controller
              key={fieldName}
              name={fieldName}
              control={control}
              rules={generateValidationRules(fieldName, fieldSchema)}
              render={({ field }) => (
                <Box>
                  <AudioPicker
                    value={(field.value as string) ?? ''}
                    onChange={(assetId, url) => handleAudioSelect(fieldName, assetId, url)}
                  />
                  {errors[fieldName] && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {errors[fieldName]?.message as string}
                    </Typography>
                  )}
                </Box>
              )}
            />
          );
        }

        const validationRules = generateValidationRules(fieldName, fieldSchema);
        const label = generateFieldLabel(fieldName, fieldSchema);
        const helperText = generateHelperText(fieldName, fieldSchema);
        const hasError = !!errors[fieldName];

        return (
          <Controller
            key={fieldName}
            name={fieldName}
            control={control}
            rules={validationRules}
            render={({ field }) => (
              <TextField
                {...field}
                label={label}
                fullWidth
                error={hasError}
                helperText={hasError ? errors[fieldName]?.message : helperText || undefined}
                placeholder={generatePlaceholder(fieldSchema)}
              />
            )}
          />
        );
      })}
    </Stack>
  );
}
